// Shared submit-toast resolver used by AddFormV1/EditFormV1.
// Admin-configured per FormGroup (group.submitMessages — see FormGroupsSection),
// generic across every module/project: no module name is ever referenced here.
//
// Defaults (no configuration anywhere, including brand-new modules):
//   add  → "<Module> has added successfully"
//   edit → "<Module> has Updated successfully"
// An admin can override any template (with ${module} substitution) or set
// suppress to skip the success toast entirely.

// Picks the configured messages from the group with the lowest `order` that
// declares any — the same resolution rule afterSubmitNav uses.
export function resolveSubmitMessages(groups) {
  const withConfig = (groups ?? [])
    .filter((g) => g?.submitMessages && typeof g.submitMessages === 'object')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return withConfig[0]?.submitMessages ?? null;
}

// "jobs" → "Jobs", "locationTaxMaster" → "LocationTaxMaster" (first letter only —
// the module key is the admin-facing name across the platform).
function moduleLabel(moduleName) {
  const raw = String(moduleName ?? '').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Record';
}

function fillTemplate(template, moduleName) {
  return String(template).replaceAll('${module}', moduleLabel(moduleName))
    .replaceAll('${moduleName}', moduleLabel(moduleName));
}

// Returns the success toast text for a finished submit, or null when the admin
// suppressed success toasts for this module. kind: 'add' | 'edit'.
export function submitSuccessMessage(groups, moduleName, kind) {
  const config = resolveSubmitMessages(groups);
  if (config?.suppress) return null;
  const template = kind === 'edit' ? config?.editSuccess : config?.addSuccess;
  if (template && String(template).trim()) return fillTemplate(template, moduleName);
  return kind === 'edit'
    ? `${moduleLabel(moduleName)} has Updated successfully`
    : `${moduleLabel(moduleName)} has added successfully`;
}

// Returns the error toast text: admin override first, then the real server
// error (most actionable), then a generic fallback. Never null — a failed
// submit must always surface.
export function submitErrorMessage(groups, moduleName, kind, serverText) {
  const config = resolveSubmitMessages(groups);
  const template = kind === 'edit' ? config?.editError : config?.addError;
  if (template && String(template).trim()) return fillTemplate(template, moduleName);
  if (serverText && String(serverText).trim()) return String(serverText);
  return kind === 'edit'
    ? `Failed to update ${moduleLabel(moduleName)}`
    : `Failed to create ${moduleLabel(moduleName)}`;
}
